import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const BeninPage = () => {
  const beninData = {
    name: 'Benin',
    region: 'West Africa',
    color: '#606090',
    metaTitle: 'Pharma & Nutraceutical Registration in Benin | CTD/WA-MRH | TheApostrophe',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Benin. DPM requirements, WA-MRH CTD expectations, timelines, documentation, and regulatory pathways for a growing West African market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Benin',
      subtitle: 'A WA-MRH–aligned West African market with high import dependence, structured dossier expectations, and rising need for chronic and preventive healthcare products.',
      cta: 'Start Your Benin Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~13.2M' },
      { label: 'Income Level', value: 'Low to lower-middle-income' },
      { label: 'Health Expenditure per Capita', value: '~USD 45-55' },
      { label: 'Health Expenditure (% of GDP)', value: '~3.6-4%' },
      { label: 'Urbanisation', value: '~48% urban' },
      { label: 'Life Expectancy', value: '61 (men), 64 (women)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Import-Driven with Regional Integration',
      stats: [
        'Benin imports nearly all of its finished pharmaceuticals',
        'Main suppliers include India, France, Belgium, China, and neighbouring West African distributors',
        'Cotonou acts as a regional logistics hub, connecting to Togo, Burkina Faso, Niger, and Nigeria',
        'National regulatory authority strengthening alignment with WA-MRH',
        'Strong demand for essential generics, antibiotics, pain/fever, antihypertensives, antidiabetics',
        'Nutraceutical & wellness products growing—immunity, vitamins/minerals, blood builders, metabolic support',
        'Pharmacies play major role in retail; OTC acceptance is high'
      ],
      opportunities: [
        'Suitable for low to mid-documentation complexity products',
        'Aligned with WA-MRH CTD structures',
        'Strategic entry point for ECOWAS markets',
        'Cotonou port provides regional distribution hub'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Landscape: Benin\'s Healthcare Needs',
      subtitle: 'High infectious disease burden with rapidly growing NCDs and significant maternal-child health needs.',
      categories: [
        {
          name: 'Infectious Diseases – Major Demand Drivers',
          items: [
            'Malaria: One of the highest regional burdens; leading cause of outpatient visits',
            'TB: Prevalence around 180-200 per 100,000',
            'HIV prevalence: ~1% nationally',
            'Diarrheal diseases remain common among children',
            'Portfolio: Antimalarials, antibiotics, ORS + zinc, paediatric antibiotics/suspensions, antifungals, antiparasitics'
          ]
        },
        {
          name: 'Growing Non-Communicable Diseases',
          items: [
            'Hypertension: Rising quickly, especially in urban areas (Cotonou, Porto-Novo)',
            'Diabetes: 2-3% but growing, driven by diet and urbanisation',
            'Cardiovascular risk increasing among middle-aged populations',
            'Portfolio: Amlodipine, telmisartan, atenolol, metformin, glimepiride, statins',
            'Low-cost chronic therapies ideal for market'
          ]
        },
        {
          name: 'Maternal & Paediatric Health',
          items: [
            'Ongoing needs for iron + folic acid',
            'Calcium + D3 for maternal health',
            'Prenatal multivitamins',
            'Dewormers (albendazole, mebendazole)',
            'Paediatric multivitamin syrups',
            'Zinc + ORS',
            'Nutra: Maternal & paediatric health supplements show very strong potential'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'DPM – Direction de la Pharmacie et du Médicament',
      description: 'Ministry of Health, Benin. Benin participates in WA-MRH (West African Medicines Regulatory Harmonization), which harmonises dossier requirements among ECOWAS states. CTD format aligned with WA-MRH for pharmaceuticals. Local MAH required for all submissions.',
      pharma: {
        timeline: 'Varies based on dossier completeness and DPM evaluation',
        requirements: [
          'CTD format aligned with WA-MRH standards',
          'Module 1 - Benin-specific administrative docs (French language)',
          'Modules 2-5 - Technical CTD documentation',
          'Stability data for tropical climate (Zone IVb expected)',
          'Indian GMP widely accepted',
          'DPM may request plant verification or additional documents',
          'Sample testing may be requested for quality confirmation',
          'MA validity period set by DPM',
          'Annual retention fees apply'
        ],
        insight: 'Benin follows CTD aligned with WA-MRH standards. DPM issues clarification requests based on CTD gaps, stability, formulation, and specifications. Local MAH mandatory.'
      },
      nutra: {
        timeline: 'Depends on classification, claims, and documentation quality',
        requirements: [
          'Full formula (quantitative)',
          'Manufacturer GMP certification',
          'COA + microbial limits',
          'Safety data for non-traditional ingredients',
          'Artwork compliance (French-language mandatory)',
          'Claims must avoid therapeutic disease-treatment wording',
          'CFS/Certificate of Free Sale required for imports'
        ],
        insight: 'Benin regulates supplements depending on claims, composition, intended use, and presentation. Growing urban consumers adopting vitamins, immune boosters, blood tonics, herbal blends, and metabolic supplements.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration (CTD – WA-MRH)',
      steps: [
        'Local MAH Appointment - Required for all submissions',
        'CTD Dossier Preparation - Module 1 (Benin-specific, French) and Modules 2-5',
        'Stability data for tropical climate (Zone IVb)',
        'GMP Requirements - Indian GMP widely accepted, DPM may request plant verification',
        'Sample Testing - May be requested for quality confirmation',
        'Query Handling - DPM issues clarification on CTD gaps, stability, formulation, specifications',
        'Marketing Authorization approval - Validity period set by DPM',
        'Annual retention fees payment'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical / Dietary Supplement Registration',
      steps: [
        'Classification determination based on claims, composition, intended use',
        'Full formula (quantitative) preparation',
        'Manufacturer GMP certification',
        'COA + microbial limits documentation',
        'Safety data for non-traditional ingredients',
        'Artwork compliance with French-language requirements',
        'Claims review to avoid therapeutic disease-treatment wording',
        'CFS/Certificate of Free Sale submission',
        'DPM evaluation and approval',
        'Marketing authorization issuance'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (CTD)': [
        'Application form (French + administrative docs)',
        'CTD Modules 1-5',
        'GMP certification',
        'CoPP (recommended)',
        'Stability data (Zone IVb)',
        'API/DMF documents',
        'Process validation',
        'Labels, inserts (French)',
        'COA and batch quality documents',
        'Samples (if requested)'
      ],
      'Nutraceuticals': [
        'Composition & COA',
        'GMP/QMS certificate',
        'Safety justification',
        'Artwork (French)',
        'Product specifications',
        'CFS / Free Sale Certificate'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Benin Should Be Part of Your West Africa Strategy',
      reasons: [
        'High import dependency - ideal for Indian-manufactured products',
        'Minimal local competition in specialized or chronic therapies',
        'Part of WA-MRH, making documentation more predictable',
        'Fast-growing urban population in Cotonou and Porto-Novo',
        'Strong demand for paediatric & maternal supplements',
        'Affordable generics preferred, making Indian pricing competitive',
        'Easy logistics via Cotonou port (regional hub for ECOWAS)',
        'Strategic gateway for West African expansion'
      ],
      bestPortfolio: [
        'Anti-infectives, antimalarials, paediatric formulations, chronic disease molecules',
        'Vitamins, blood tonics, and herbal nutraceuticals',
        'Maternal & child health products'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Register in Benin',
      services: [
        'Product-market feasibility analysis',
        'WA-MRH CTD dossier preparation',
        'French artwork & label compliance',
        'Nutraceutical documentation support',
        'Safety & ingredient acceptability checks',
        'Importer/MAH coordination',
        'Query handling, renewals, and lifecycle support'
      ]
    },
    
    faqs: [
      {
        question: 'Does Benin require CTD format for pharmaceuticals?',
        answer: 'Yes. Benin follows the CTD aligned with WA-MRH standards.'
      },
      {
        question: 'Is French-language labelling mandatory?',
        answer: 'Yes. Pharmaceuticals and supplements must have French labels for local sale.'
      },
      {
        question: 'Is Indian GMP accepted?',
        answer: 'Yes. Indian GMP is accepted for dossier submissions. DPM may request plant verification or additional documents.'
      },
      {
        question: 'Do supplements require registration?',
        answer: 'Yes. Supplements must be approved before being marketed in Benin.'
      },
      {
        question: 'Is Benin a good gateway market for West Africa?',
        answer: 'Yes. Cotonou port and WA-MRH participation make Benin a strategic entry point for ECOWAS markets.'
      }
    ]
  };

  return <CountryTemplate countryData={beninData} />;
};
